import { useMemo, useState } from 'react';
import { useLang } from 'hooks/lang';

import { loaders, EnrichedProjectInvoice } from 'entities';

import { Column, Row, Checkbox, Button, Tabs, Skeleton, Table } from 'components/Blocks';

import { CirclePlusIcon, PencilIcon } from 'components/Icons';
import { CreateInvoiceModal } from 'pages/Invoice/components';

import { NoInvoiceFound } from '../NotFound';

import { INVOICE_COUNTRIES } from 'utils/countries';
import { getFirst } from 'utils/array';

import { formatCurrency } from 'utils/text';

function findProjectInvoice(
  list: EnrichedProjectInvoice[],
  wantedId: string,
): EnrichedProjectInvoice | undefined {
  return list.find(({ id }) => wantedId === id);
}

export function ReadyToInvoice() {
  const [tab, setTab] = useState('');

  const { t } = useLang();

  const [creating, setCreating] = useState<EnrichedProjectInvoice[]>([]);
  const [selected, setSelected] = useState<EnrichedProjectInvoice[]>([]);

  const { data, overwrite, errorOrLoading } = loaders.invoice.projectInvoicesReadyToInvoice.use({
    defaultValue: [],

    onDataChange: (invoices) =>
      setTab(
        (old) =>
          old ||
          INVOICE_COUNTRIES.find(({ value }) => invoices.find(({ country }) => country === value))
            ?.value ||
          '',
      ),
  });

  const countryTabs = INVOICE_COUNTRIES.map(({ label, value }) => ({
    value,
    label,
    render: !!data.find(({ country }) => country === value),
  })).filter((t) => t.render);

  const handleSelectionToggle = (projectInvoice: EnrichedProjectInvoice): void => {
    setSelected((current) => {
      const isSelected = !!selected.find(({ id }) => projectInvoice.id === id);

      return isSelected
        ? current.filter(({ id }) => id !== projectInvoice.id)
        : [...current, projectInvoice];
    });
  };

  const handleCreationClose = (created = false): void => {
    setCreating([]);

    if (!created) return;

    setSelected([]);

    const leftOver = data.filter(({ id }) => !findProjectInvoice(creating, id));

    const projectOnSameCountry = leftOver.find(({ country }) => country === tab);

    if (leftOver.length && !projectOnSameCountry) setTab(getFirst(leftOver).country);

    overwrite(leftOver);
  };

  const projectInvoicesOnCountry = data.filter(({ country }) => country === tab);

  const creationEntities = useMemo(
    () => creating.map(({ project, ...rest }) => ({ ...rest, name: project.name })),
    [creating],
  );

  if (errorOrLoading) return <Skeleton quantity={8} height={36} />;

  return (
    <>
      <Column>
        <Row justifyContent="space-between">
          <Tabs
            fullWidth={false}
            tabs={countryTabs}
            onTabChange={setTab}
            value={tab}
            responsiveBreakpoint="PHONE"
          />

          {!!selected.length && (
            <Button onClick={() => setCreating(selected)} variant="outlined">
              <PencilIcon />
              {`${t('create-from')} ${selected.length}`}
            </Button>
          )}
        </Row>

        <NoInvoiceFound render={!errorOrLoading && !projectInvoicesOnCountry.length}>
          <CreateInvoiceModal
            close={() => setCreating([])}
            onCreateSuccess={() => handleCreationClose(true)}
            entities={creationEntities}
            allowBalances
            payloadGenerator={() => ({
              type: 'PROJECT',
              entries: creating.map(({ project, id }) => ({
                project: project.id,
                id,
                type: 'PROJECT',
              })),
            })}
          />

          <Table
            items={projectInvoicesOnCountry}
            spacing="medium"
            uniqueKey="id"
            columns={[
              {
                className: 'w-10',
                dataClassName: 'pr-1',
                render: (projectInvoice) => {
                  const isSelected = !!findProjectInvoice(selected, projectInvoice.id);

                  const isDisabled = !!selected.find(({ client, currency, country }) =>
                    [
                      client !== projectInvoice.client,
                      currency !== projectInvoice.currency,
                      country !== projectInvoice.country,
                    ].some(Boolean),
                  );

                  return (
                    <Checkbox
                      onChange={() => handleSelectionToggle(projectInvoice)}
                      checked={isSelected}
                      disabled={isDisabled}
                    />
                  );
                },
              },
              {
                label: t('client') as string,
                property: 'clientName',
                filter: true,
                sort: true,
              },
              {
                label: t('value') as string,
                render: ({ value, currency }) => formatCurrency(value, currency),
                getTextValue: ({ currency }) => currency,
                filter: true,
                sort: true,
                sorter: (a, b) => {
                  const [aMonetary, bMonetary] = [a, b].map(({ currency, value }) => ({
                    currency,
                    value,
                  }));

                  if (aMonetary.currency !== bMonetary.currency)
                    return aMonetary.currency.localeCompare(bMonetary.currency);

                  const [valueA, valueB] = [aMonetary.value, bMonetary.value].map(Number);

                  return valueA - valueB;
                },
              },
              {
                label: t('extra-information') as string,
                property: 'extraInformation',
                search: true,
              },
              {
                label: t('project') as string,
                search: true,
                getTextValue: ({ project }) => project.name,
                render: ({ project }) => (
                  <Button to="projectProfile" params={{ projectId: project.id }} variant="link">
                    {project.name}
                  </Button>
                ),
              },
              {
                className: 'w-[200px]',
                render: (projectInvoice) => (
                  <Button
                    variant="ghost"
                    disabled={!!findProjectInvoice(selected, projectInvoice.id)}
                    onClick={() => setCreating([projectInvoice])}
                  >
                    <CirclePlusIcon />
                    {t('create-invoice')}
                  </Button>
                ),
              },
            ]}
          />
        </NoInvoiceFound>
      </Column>
    </>
  );
}
